import { type FC, useCallback, useEffect, useRef, useState } from 'react'

import { LoadingBar } from '@/components/ui/LoadingBar.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { MediaSkeleton } from '@/dialogs/ChatGalleryDialog/MediaSkeleton.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import type { DialogProps } from '@/dialogs/types'
import { openDialog } from '@/models/dialogs.ts'
import { useActiveChatGallery } from '@/modules/chatting/hooks/use-active-chat-gallery.ts'
import { ChatMediaType } from '@/modules/chatting/types.ts'
import { useInViewPort } from '@/modules/chatting/ui/hooks/useInViewport.ts'

import { ChatGalleryFilters } from './ChatGalleryFilters.tsx'
import { ChatGalleryMediaItem } from './ChatGalleryMediaItem.tsx'

interface Props extends DialogProps {}

export const ChatGalleryDialog: FC<Props> = ({ isOpen, onClose }) => {
  const bottomRef = useRef<HTMLDivElement>(null)
  const inBottom = useInViewPort(bottomRef, { threshold: 0.5 })
  const [selectedMediaType, setSelectedMediaType] = useState<ChatMediaType>(
    ChatMediaType.All,
  )
  const {
    hasMore,
    isLoading,
    isEmpty,
    fetchNextPage,
    isFetchingNextPage,
    allMedias,
  } = useActiveChatGallery({
    selectedMediaType,
  })

  useEffect(() => {
    if (isOpen && inBottom && hasMore && !isLoading && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasMore, inBottom, isFetchingNextPage, isLoading, isOpen])

  const handleMediaPreview = useCallback(
    (mediaId: number) => {
      openDialog({
        type: DIALOG_TYPE.VAULT_MEDIA_PREVIEW,
        mediaList: allMedias.map(([{ id }]) => id),
        mediaId,
      })
    },
    [allMedias],
  )

  return (
    <Dialog
      onClose={onClose}
      panelClassName="mt-0 sm:mt-0"
      bigDialog
      show={isOpen}
    >
      <Dialog.Header>
        <Dialog.Title>Chat Gallery</Dialog.Title>
      </Dialog.Header>
      <div className="py-6 @container relative">
        <ChatGalleryFilters
          onSelect={setSelectedMediaType}
          selected={selectedMediaType}
        />
        <div className="mt-4 grid max-h-[50vh] grid-cols-3 gap-1 overflow-auto">
          {!isLoading && (isEmpty || !allMedias.length) && (
            <div className="col-span-full flex h-20 items-center justify-center">
              <p className="text-neutral-500">No media found</p>
            </div>
          )}
          {allMedias.map(([media, message]) => (
            <div
              className="col-span-1 h-fit"
              key={`g-${message.id}-m-${media.id}`}
              onClick={() => {
                handleMediaPreview(media.id)
              }}
            >
              <ChatGalleryMediaItem
                key={media.id + message.id}
                media={media}
                message={message}
                onClose={onClose}
              />
            </div>
          ))}
          {(isFetchingNextPage || isLoading) &&
            !allMedias.length &&
            Array.from({ length: 6 }).map((_, i) => <MediaSkeleton key={i} />)}
          {hasMore && (
            <div
              className="col-span-full flex h-20 items-center justify-center"
              ref={bottomRef}
            />
          )}
        </div>
        {(isFetchingNextPage || isLoading) && (
          <LoadingBar rootClassName="absolute bottom-auto" />
        )}
      </div>
      <div className="w-full">
        <Button
          variant="outline"
          className="w-full"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </Dialog>
  )
}
